import React from 'react';
import GoogleMapReact from 'google-map-react';
import IncidentMarker from './IncidentMarker';
import { useThemeMode } from '../../context/ThemeContext';
import { DARK_MAP_STYLE, LIGHT_MAP_STYLE } from '../../config/mapTheme';
import { haversineDistanceKm } from '../../utils/utils';

// AnnouncementMember.lat/lng is only ever set once, at accept/reject time — the mobile
// app's 60s background heartbeat updates Member.lat/lng instead (it doesn't send an
// assignmentId, so the server's telemetry handler never touches the per-assignment
// field). member.lat/lng is therefore the one that's actually live; the top-level
// lat/lng is a same-shape fallback for the brief window before a member's first
// heartbeat lands.
function livePosition(m) {
    const lat = m.member?.lat ?? m.lat;
    const lng = m.member?.lng ?? m.lng;
    return { lat, lng };
}

// An incident created without picking a location has null coordinates, which callers
// turn into 0/NaN with Number() — 0,0 is in the Gulf of Guinea, not "unknown". Treat
// anything that isn't a real coordinate as no location.
function hasCoordinates(lat, lng) {
    const la = Number.parseFloat(lat);
    const ln = Number.parseFloat(lng);
    return Number.isFinite(la) && Number.isFinite(ln) && !(la === 0 && ln === 0);
}

function IncidentMap({ lat = 24.491241, lng = 54.37831961, details = '', members = [] }) {
    const { mode } = useThemeMode();
    const located = hasCoordinates(lat, lng);

    const handleApiLoaded = (map, maps) => {
        const bounds = new maps.LatLngBounds();
        let points = 0;
        if (located) {
            bounds.extend(new maps.LatLng(lat, lng));
            points += 1;
        }

        // Extend bounds to include additional members' markers
        members.forEach((m) => {
            const pos = livePosition(m);
            if (pos.lat && pos.lng) {
                bounds.extend(new maps.LatLng(pos.lat, pos.lng));
                points += 1;
            }
        });

        // Nothing to frame — stay on the default (Abu Dhabi) view.
        if (points === 0) return;
        map.fitBounds(bounds);

        const listener = maps.event.addListener(map, 'idle', () => {
            // Was checking > 10 but clamping to 12 — a bounds-fit that landed at 11 would
            // paradoxically zoom IN. Using the same value for both keeps this a one-way
            // "never zoom in past this" clamp, consistent with DashboardMap's minZoom.
            if (map.getZoom() > 8) {
                map.setZoom(8);
            }
            maps.event.removeListener(listener);
        });
    };

    return (
        <div className="map-wrap map-mini h-full w-full">
            <GoogleMapReact
                bootstrapURLKeys={{ key: 'AIzaSyCI8O91FfQgfevadyNo9pKNt52MT5RZp_4' }}
                defaultCenter={{
                    lat: 24.491241,
                    lng: 54.37831961,
                }}
                defaultZoom={11}
                options={{ styles: mode === 'dark' ? DARK_MAP_STYLE : LIGHT_MAP_STYLE }}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => {
                    handleApiLoaded(map, maps)
                }}
            >
                {located && (
                    <IncidentMarker
                        lat={lat}
                        lng={lng}
                        text={details}
                    />
                )}
                {members.map((m) => {
                    const { lat: memberLat, lng: memberLng } = livePosition(m);
                    const distanceKm = located && memberLat && memberLng
                        ? haversineDistanceKm(Number.parseFloat(memberLat), Number.parseFloat(memberLng), Number.parseFloat(lat), Number.parseFloat(lng))
                        : null;
                    return (
                        m.accepted && memberLat && memberLng && <IncidentMarker
                            key={m.id}
                            lat={memberLat}
                            lng={memberLng}
                            role={m.member?.role?.role_name}
                            name={m.member?.fullname}
                            team={m.team?.name}
                            distance={distanceKm != null ? distanceKm.toFixed(1) : null}
                            lastSeenAt={m.member?.lastSeenAt}
                            showDetails={true}
                        />
                    );
                })}
            </GoogleMapReact>
        </div>
    );
}

export default IncidentMap;
